import type { AxiosProgressEvent } from 'axios';
import axios from 'axios';
import { defineStore } from 'pinia';
import { ref } from 'vue';

import { useApiStore, useToastStore } from '@nora-monorepo/ui-shared';

import type { FileUpload } from '#nora-chat-ui/components/open-conversations/messages/file';
import { useAttachmentApiStore } from '#nora-chat-ui/stores/api/attachment-api-store';

export const useAttachmentStore = defineStore('attachmentStore', () => {
  const uploads = ref<FileUpload[]>([]);
  const api = useApiStore();
  const attachmentApiStore = useAttachmentApiStore();
  const toast = useToastStore();

  /**
   * Removes upload by upload id.
   *
   * Does not cancel in-flight uploads. The reason is that `AbortController()` logic would be complex weighed up against the benefit.
   * If a user navigates away or deletes the upload while it's in-flight, the upload can simply be deleted in the Files browser. This is not ideal but also not a huge issue.
   * @param id
   */
  const removeUploadById = async (id: FileUpload['id']): Promise<void> => {
    const uploadToRemove = uploads.value.find(upload => upload.id === id);

    if (!uploadToRemove) {
      return;
    }

    // A file that has been uploaded but not sent yet, is not considered an attachment. So we delete it from the file API directly.
    if (uploadToRemove.fileId) {
      try {
        await api.file.deleteFile({
          domain: 'conversation',
          domainId: uploadToRemove.conversationId,
          fileId: uploadToRemove.fileId,
          path: '/',
        });
      }
      catch {
        toast.addError({ message: `Failed to delete attachment` });
        return;
      }
    }
    uploads.value = uploads.value.filter(upload => upload.id !== id);
  };

  /**
   * Calls the API to upload a message attachment.
   * @param upload
   */
  const executeAttachmentUpload = async (upload: FileUpload): Promise<FileUpload> => {
    const uploadUrl = await api.unwrap(
      api.conversationMessageAttachment.getConversationUploadUrl({
        conversationId: upload.conversationId,
      }),
    );

    if (!uploadUrl.data?.url) {
      throw new Error('Could not get upload url');
    }

    try {
      const uploadedFile = await attachmentApiStore.uploadFile(
        uploadUrl.data,
        upload.file,
        upload.onProgressEvent,
        upload.cancelTokenSource,
      );

      upload.fileId = uploadedFile.id;
      upload.status = 'success';
      upload.urls = uploadedFile.urls;
      upload.progress = 100;
    }
    catch (error) {
      console.error('Error uploading file:', error);

      upload.status = 'file-upload-error';
    }

    return upload;
  };

  const retryUpload = async (uploadIndex: number): Promise<void> => {
    const upload = uploads.value[uploadIndex];

    if (!upload) {
      throw new Error('Tried to retry an upload that doesnt exist');
    }

    const newUploads = [...uploads.value];
    newUploads[uploadIndex] = await executeAttachmentUpload(upload);
    uploads.value = newUploads;
  };

  const onUploadProgress = (
    progressEvent: AxiosProgressEvent,
    uploadIndex: number | string,
  ): void => {
    const newProgress = Math.round(
      (progressEvent.loaded * 100)
      / (progressEvent.total ? progressEvent.total : 1),
    );
    const newOffset = progressEvent.loaded;

    let upload: FileUpload | undefined;

    if (typeof uploadIndex === 'number') {
      upload = uploads.value[uploadIndex];
    }
    else {
      upload = uploads.value.find(upload => upload.id === uploadIndex);
    }

    if (!upload) {
      return;
    }

    upload.progress = Math.min(newProgress, 100);
    upload.offset = newOffset;
  };

  /**
   * Uploads a given file to the namespace for the given conversation Id
   * @param conversationId
   * @param file
   * @param preview
   * @param offset
   * @param height
   * @param width
   */
  const uploadFile = async (
    conversationId: string,
    file: File,
    preview: string,
    offset = 0,
    height?: number,
    width?: number,
  ): Promise<void> => {
    const uploadIndex: number = uploads.value.length;
    const generatedId: string = crypto.randomUUID();

    const upload: FileUpload = {
      id: generatedId,
      cancelTokenSource: axios.CancelToken.source(),
      conversationId,
      progress: 0,
      file,
      isCompleted: false,
      offset,
      preview,
      status: 'saving-file',
      onProgressEvent: (progressEvent) => {
        onUploadProgress(progressEvent, generatedId);
      },
      height,
      width,
    };

    uploads.value = [...uploads.value, upload];
    const uploadedFile = await executeAttachmentUpload(upload);
    uploads.value = uploads.value.map((upload, index) => {
      if (index === uploadIndex) {
        return uploadedFile;
      }
      return upload;
    });
  };

  const clearUploadsForConversation = (conversationId: string): void => {
    const nextUploads = uploads.value.filter(
      upload => upload.conversationId !== conversationId,
    );

    uploads.value = nextUploads;
  };

  return {
    uploads,
    retryUpload,
    removeUploadById,
    uploadFile,
    clearUploadsForConversation,
  };
});
